// D-126 (owner, 2026-11-04): the account's own mobile number, for "Confirm it's you" by text.
// Adding it needs a step-up first (the server marks /me/phone/start sensitive), then a 6-digit code
// sent to the new number, paid with 1 SMS credit by a shop the person belongs to. Removing it also
// needs a step-up. Codes by text are only offered to accounts without an authenticator or passkey
// (the server never accepts them from those), and only while the shop has SMS credits.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { normalizeDigits } from "@ady/format";
import { t } from "@ady/i18n";
import { Banner, Button, Field, Input, Select, useToast } from "@ady/ui";
import { ApiError, api } from "../api/client";
import { errorMessage } from "../api/errors";
import { keys, useMe } from "../api/queries";
import { isStepUpCancelled, useStepUp } from "./StepUp";

function phoneError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === "notify.insufficient_credits") return t("security.phone.noCredits");
    if (err.code === "validation.phone_taken") return t("security.phone.taken");
    if (err.code === "validation.invalid_phone") return t("security.phone.invalid");
    if (err.code === "dependency.activation_required") return t("security.phone.inactive");
  }
  return errorMessage(err);
}

export function PhoneCard() {
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const stepUp = useStepUp();
  const user = me.data?.user;
  const shops = me.data?.memberships.map((m) => m.org) ?? [];
  const [phone, setPhone] = useState("");
  const [orgId, setOrgId] = useState("");
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const payer = orgId || shops[0]?.id || "";
  const strong = Boolean(user && (user.mfa.totp || user.mfa.passkeys > 0));

  const refresh = () => qc.invalidateQueries({ queryKey: keys.me });

  const send = async () => {
    setError(undefined);
    const digits = normalizeDigits(phone).replace(/[^\d+]/g, "");
    if (digits.replace(/\D/g, "").length < 10) return setError(t("security.phone.invalid"));
    if (!payer) return setError(t("security.phone.noShop"));
    setBusy(true);
    try {
      const res = await stepUp(() => api.phoneStart(digits, payer));
      setChallenge(res.challenge_id);
      setCode("");
    } catch (err) {
      if (!isStepUpCancelled(err)) setError(phoneError(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setError(undefined);
    if (!challenge) return;
    if (!/^\d{6}$/.test(code)) return setError(t("stepUp.codeInvalid"));
    setBusy(true);
    try {
      await api.phoneVerify(challenge, code);
      setChallenge(null);
      setPhone("");
      setCode("");
      await refresh();
      toast.show({ tone: "success", title: t("security.phone.added") });
    } catch (err) {
      setError(phoneError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setError(undefined);
    setBusy(true);
    try {
      await stepUp(() => api.phoneRemove());
      await refresh();
      toast.show({ tone: "success", title: t("security.phone.removed") });
    } catch (err) {
      if (!isStepUpCancelled(err)) setError(phoneError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-3 rounded-md border border-subtle bg-surface p-5"
      data-testid="security-phone"
    >
      <h2 className="text-title-sm font-semibold">{t("security.phone.title")}</h2>
      <p className="text-md text-fg-secondary">{t("security.phone.body")}</p>
      {strong ? <Banner kind="info" title={t("security.phone.strongNote")} /> : null}
      {error ? <Banner kind="error" title={error} /> : null}
      {user?.phone ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="ady-ledger text-md font-medium" data-content="user">
            {user.phone}
          </span>
          <span className="text-xs text-fg-tertiary">{t("security.phone.verified")}</span>
          <Button variant="secondary" onClick={() => void remove()} loading={busy}>
            {t("security.phone.remove")}
          </Button>
        </div>
      ) : challenge ? (
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t("stepUp.codeLabel")} help={t("security.phone.codeHelp", { phone })}>
            <Input
              value={code}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              ledger
              autoFocus
              onChange={(e) =>
                setCode(normalizeDigits(e.target.value).replace(/\D/g, "").slice(0, 6))
              }
              data-testid="security-phone-code"
            />
          </Field>
          <Button onClick={() => void verify()} loading={busy}>
            {t("security.phone.confirm")}
          </Button>
          <Button variant="link" onClick={() => setChallenge(null)}>
            {t("security.phone.change")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <Field label={t("security.phone.label")} help={t("security.phone.help")}>
            <Input
              value={phone}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder={t("security.phone.placeholder")}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="security-phone-input"
            />
          </Field>
          {shops.length > 1 ? (
            <Field label={t("security.phone.payer")}>
              <Select
                options={shops.map((s) => ({ value: s.id, label: s.name }))}
                value={payer}
                onChange={(e) => setOrgId(e.target.value)}
              />
            </Field>
          ) : null}
          <Button onClick={() => void send()} loading={busy} data-testid="security-phone-send">
            {t("security.phone.send")}
          </Button>
        </div>
      )}
    </section>
  );
}
